import React, { useState, useEffect } from "react";
import { Box, Slider as MaterialSlider } from "@mui/material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import AutocompleteInput from "../../Components/AutocompleteInput";
import { smileys, subTypes } from "../itemTypes";
import SmileyDialog from "./SmileyDialog";
import { useTranslation } from "react-i18next";
import getSliderMark from "./getSliderMark";
import getQuestion from "../getQuestion";

const defaultMarkValuesLeft = [0, 1];
const defaultMarkValuesRight = [7, 100];

export default function Slider(props) {
  const { question, updateHandler, isSelected, selected, disabled } = props;
  const { t } = useTranslation();
  const defaultMarkLabelsLeft = [
    { type: "neutral", label: t("editor.questionTypes.slider.neutral") },
    {
      type: "not at all",
      label: t("editor.questionTypes.likert.not at all"),
    },
  ];
  const defaultMarkLabelsRight = [
    {
      type: "extremely",
      label: t("editor.questionTypes.likert.extremely"),
    },
  ];
  const isSmiley = question.type === "smiley";
  const [openSmiley, setOpenSmiley] = useState(false); //isSelected && isSmiley
  const smileyButtonText = smileys?.[question.typeSmiley]
    ? t(`editor.questionTypes.smiley.${smileys[question.typeSmiley]}`) // display the smiley name
    : t("editor.questionTypes.smiley.addSmiley"); // display "Add smiley"
  const elementId = selected.split("#")?.[1] ?? "";
  const isLeftValueSelected = elementId === "leftValue";
  const isRightValueSelected = elementId === "rightValue";
  const isLeftLabelSelected = elementId === "leftLabel";
  const isRightLabelSelected = elementId === "rightLabel";
  const handleClose = () => setOpenSmiley(false);

  const handleUpdateMark = (index, obj) => {
    const getNewMarks = () => {
      // update left mark
      if (index === 0)
        return [
          { ...question.marks[0], ...obj },
          getSliderMark(question.marks[1]),
        ];
      // update right mark
      if (index === 1)
        return [
          getSliderMark(question.marks[0]),
          { ...question.marks[1], ...obj },
        ];
      return question.marks;
    };
    // update the question marks
    const newMarks = getNewMarks(index, obj);
    updateHandler({ marks: newMarks });
  };

  const handleMark =
    ({ index, key }) =>
    (event, value, reason) => {
      if (reason === "reset") return;
      if (reason === "selectOption" && key === "label") {
        // select predefined label
        handleUpdateMark(index, value);
      }
      if (reason === "input" && key === "label") {
        // custom label
        handleUpdateMark(index, { type: "", label: value });
      }
      if (key === "value") {
        // convert the string to a number
        const number = parseInt(value);
        const inputValue = isNaN(number) ? value : number;
        handleUpdateMark(index, { value: inputValue });
      }
    };

  return (
    <Box display="flex" justifyContent="space-between" textAlign="end">
      <Box width="80%">
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            visibility: isSelected ? "visible" : "hidden",
          }}
        >
          <Box width="65%" display="flex" justifyContent="space-between">
            <Box width={80}>
              <AutocompleteInput
                options={defaultMarkValuesLeft}
                getOptionLabel={(option) => `${option}`}
                disabled={isSmiley || disabled}
                value={isSmiley ? 0 : question.marks[0].value}
                onChange={handleMark({ index: 0, key: "value" })}
                textAlign="left"
                error={typeof question.marks[0].value !== "number"}
                isSelected={isLeftValueSelected}
              />
            </Box>
            <Box width={80}>
              <AutocompleteInput
                options={defaultMarkValuesRight}
                getOptionLabel={(option) => `${option}`}
                disabled={isSmiley || disabled}
                value={isSmiley ? 100 : question.marks[1].value}
                onChange={handleMark({ index: 1, key: "value" })}
                textAlign="right"
                error={typeof question.marks[1].value !== "number"}
                isSelected={isRightValueSelected}
              />
            </Box>
          </Box>
        </Box>
        <Box display="flex" marginTop={-1}>
          <Box
            marginRight={1}
            sx={{
              visibility: isSmiley ? "hidden" : "visible",
            }}
          >
            <AutocompleteInput
              options={defaultMarkLabelsLeft}
              value={question.marks[0]}
              getOptionLabel={(option) => option.label}
              onChange={handleMark({ index: 0, key: "label" })}
              textAlign="left"
              disabled={disabled}
              isSelected={isLeftLabelSelected}
            />
          </Box>

          <Box width={"100%"}>
            <MaterialSlider
              disabled
              defaultValue={50}
              aria-labelledby="discrete-slider-custom"
              step={1}
              sx={{ color: "rgb(168 168 168)" }}
              size="small"
            />
          </Box>

          <Box
            marginLeft={1}
            sx={{
              visibility: isSmiley ? "hidden" : "visible",
            }}
          >
            <AutocompleteInput
              options={defaultMarkLabelsRight}
              value={question.marks[1]}
              getOptionLabel={(option) => option.label}
              onChange={handleMark({ index: 1, key: "label" })}
              textAlign="right"
              disabled={disabled}
              isSelected={isRightLabelSelected}
            />
          </Box>
        </Box>
      </Box>

      <SmileyDialog
        open={openSmiley}
        handleClose={handleClose}
        typeSmiley={question.typeSmiley}
        handleOk={(type) => {
          const value =
            type === "null"
              ? { type: "slider", typeSmiley: "null" } // make normal slider
              : {
                  type: "smiley",
                  typeSmiley: type,
                  marks: getQuestion(subTypes.slider).marks,
                }; // make smiley slider with default slider marks
          updateHandler(value);
          handleClose();
        }}
      />

      <Box display={isSelected ? "flex" : "none"}>
        <Box marginTop="auto">
          <Button
            onClick={() => setOpenSmiley(true)}
            endIcon={isSmiley ? <ArrowDropDownIcon /> : null}
            id="addSmileyButton"
            disabled={disabled}
          >
            <Typography variant="body2">{smileyButtonText}</Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
